import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CafeLogForm } from "../components/CafeLogForm";
import { useCafeLog } from "../hooks/useCafeLogs";
import { api, type ParseUsage } from "../lib/api";
import type { CafeLog, CafeLogInput, CoffeePhoto } from "../types";
import { photoUrl } from "../lib/photos";
import { prepImageForUpload } from "../lib/image";

// Fields AI's coffee parser returns that map cleanly onto a café-log bean section.
function mapParsed(parsed: Record<string, unknown>): Partial<CafeLogInput> {
  const out: Partial<CafeLogInput> = {};
  const str = (k: string) => (typeof parsed[k] === "string" && parsed[k] ? (parsed[k] as string) : undefined);
  if (str("kind")) out.kind = str("kind");
  if (str("variety")) out.variety = str("variety");
  if (str("process")) out.process = str("process");
  if (str("country")) out.country = str("country");
  if (str("region")) out.region = str("region");
  if (str("roaster")) out.roaster = str("roaster");
  if (Array.isArray(parsed.flavor_tags) && parsed.flavor_tags.length > 0) {
    out.flavor_tags = (parsed.flavor_tags as unknown[]).map(String);
  }
  return out;
}

export function CafeLogEntry() {
  const { id: idParam } = useParams();
  const nav = useNavigate();
  const editing = idParam != null && idParam !== "new" && Number.isFinite(Number(idParam));
  const id = editing ? Number(idParam) : null;
  const { data: loaded, error: loadError, refresh } = useCafeLog(id);

  const [prefill, setPrefill] = useState<Partial<CafeLogInput> | undefined>(undefined);
  const liveRef = useRef<CafeLogInput | null>(null);

  const [pendingPhotos, setPendingPhotos] = useState<File[]>([]);
  const [parsing, setParsing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<ParseUsage | null>(null);

  // Seed the form from the loaded entry once (edit mode).
  useEffect(() => {
    if (loaded) setPrefill(toInput(loaded));
  }, [loaded]);

  const onAiFill = async () => {
    if (pendingPhotos.length === 0) {
      setError("Pick at least one photo first, then tap AI fill.");
      return;
    }
    setParsing(true); setError(null); setUsage(null);
    if (pendingPhotos.length > 6) setError(`only the first 6 of ${pendingPhotos.length} photos are sent`);
    try {
      const images = await Promise.all(pendingPhotos.slice(0, 6).map((f) => prepImageForUpload(f)));
      const { data: parsed, usage } = await api.parseImages(images);
      const mapped = mapParsed(parsed as Record<string, unknown>);
      // Merge onto the user's current edits, not the stale initial.
      setPrefill({ ...(liveRef.current || {}), ...mapped });
      setUsage(usage);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setParsing(false);
    }
  };

  // Upload all pending photos to a log id, surfacing per-photo progress.
  const uploadPending = async (logId: number): Promise<string[]> => {
    const failures: string[] = [];
    if (pendingPhotos.length === 0) return failures;
    setUploadProgress({ done: 0, total: pendingPhotos.length });
    try {
      for (let i = 0; i < pendingPhotos.length; i++) {
        try { await api.cafeLogs.uploadPhoto(logId, pendingPhotos[i]); }
        catch (e) { failures.push(`#${i + 1} (${pendingPhotos[i].name}): ${(e as Error).message}`); }
        setUploadProgress({ done: i + 1, total: pendingPhotos.length });
      }
    } finally {
      setUploadProgress(null);
    }
    return failures;
  };

  const onSubmit = async (input: CafeLogInput) => {
    if (editing && id != null) {
      await api.cafeLogs.update(id, input);
      const failures = await uploadPending(id);
      setPendingPhotos([]);
      refresh();
      if (failures.length > 0) alert(`Saved, but ${failures.length} photo(s) failed:\n\n${failures.join("\n")}`);
      else nav("/cafe");
    } else {
      const created = await api.cafeLogs.create(input);
      const failures = await uploadPending(created.id);
      if (failures.length > 0) {
        alert(`Entry saved, but ${failures.length} photo(s) failed to upload:\n\n${failures.join("\n")}\n\nYou can add them by opening the entry from the Café Log.`);
      }
      nav("/cafe");
    }
  };

  const onDeleteEntry = async () => {
    if (id == null) return;
    if (!confirm("Delete this café log entry and its photos? This can't be undone.")) return;
    await api.cafeLogs.remove(id);
    nav("/cafe");
  };

  if (editing && loadError) return <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm">{loadError}</div>;
  if (editing && !loaded) return <p className="text-paper-500 text-sm">Loading…</p>;

  const submitLabel = pendingPhotos.length > 0
    ? `${editing ? "Save" : "Create"} + upload ${pendingPhotos.length} photo${pendingPhotos.length === 1 ? "" : "s"}`
    : editing ? "Save changes" : "Create entry";

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-display text-3xl">{editing ? "Edit entry" : "New café entry"}</h2>
        {editing ? (
          <button onClick={onDeleteEntry} className="text-[11px] uppercase tracking-widest text-paper-400 hover:text-red-700">Delete</button>
        ) : null}
      </div>

      {parsing ? (
        <Banner>Reading the photo with AI… 10–30 s on mobile.</Banner>
      ) : null}
      {uploadProgress ? (
        <Banner>Uploading photo {Math.min(uploadProgress.done + 1, uploadProgress.total)} of {uploadProgress.total}… cellular uploads can take 10–30 s each.</Banner>
      ) : null}
      {error ? <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div> : null}
      {usage ? (
        <div className="text-[11px] uppercase tracking-widest text-paper-400">
          {usage.input.toLocaleString()} in · {usage.output.toLocaleString()} out{usage.reasoning ? ` · ${usage.reasoning} reasoning` : ""} · ≈{usage.cents}¢ · {usage.model}
        </div>
      ) : null}

      <CafeLogForm
        initial={prefill}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        onCancel={() => nav("/cafe")}
        onChange={(v) => { liveRef.current = v; }}
      >
        <PhotoBlock
          log={editing ? loaded : null}
          pending={pendingPhotos}
          setPending={setPendingPhotos}
          parsing={parsing}
          onAiFill={onAiFill}
          onChanged={refresh}
        />
      </CafeLogForm>
    </div>
  );
}

function PhotoBlock({ log, pending, setPending, parsing, onAiFill, onChanged }: {
  log: CafeLog | null;
  pending: File[];
  setPending: React.Dispatch<React.SetStateAction<File[]>>;
  parsing: boolean;
  onAiFill: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const deleteExisting = async (photo: CoffeePhoto) => {
    if (!log) return;
    if (!confirm("Remove this photo?")) return;
    setBusy(true);
    try { await api.cafeLogs.deletePhoto(log.id, photo.id); onChanged(); }
    finally { setBusy(false); }
  };

  const makePrimary = async (photo: CoffeePhoto) => {
    if (!log || log.photos[0]?.id === photo.id) return;
    const ordered = [photo.id, ...log.photos.filter((p) => p.id !== photo.id).map((p) => p.id)];
    setBusy(true);
    try { await api.cafeLogs.reorderPhotos(log.id, ordered); onChanged(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="eyebrow border-b border-paper-200 pb-1">Photos</div>

      {/* Existing (already-saved) photos — edit mode only */}
      {log && log.photos.length > 0 ? (
        <ul className="flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          {log.photos.map((p, i) => (
            <li key={p.id} className="relative w-24 h-24 shrink-0 border border-paper-200 bg-paper-100 overflow-hidden group">
              <img src={photoUrl(p.r2_key)!} alt="" className="h-full w-full object-cover" />
              {i === 0 ? (
                <span className="absolute top-1 left-1 text-[8pt] uppercase tracking-widest bg-ink text-paper-50 px-1">primary</span>
              ) : (
                <button type="button" disabled={busy} onClick={() => makePrimary(p)} title="Make primary"
                  className="absolute bottom-1 left-1 text-[8pt] uppercase tracking-widest bg-paper-50/90 border border-paper-300 px-1">★</button>
              )}
              <button type="button" disabled={busy} onClick={() => deleteExisting(p)}
                className="absolute top-0 right-0 w-5 h-5 bg-paper-50 border border-paper-300 text-xs">×</button>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Newly-picked photos, queued for upload on save */}
      {pending.length > 0 ? (
        <div className="space-y-1">
          <div className="eyebrow">{pending.length} new photo{pending.length === 1 ? "" : "s"} · uploaded on save</div>
          <ul className="flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            {pending.map((f, i) => (
              <li key={i} className="relative w-24 h-24 shrink-0 border border-paper-200 bg-paper-100 overflow-hidden">
                <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover"
                  onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)} />
                <button type="button" onClick={() => setPending((cur) => cur.filter((_, idx) => idx !== i))}
                  className="absolute top-0 right-0 w-5 h-5 bg-paper-50 border border-paper-300 text-xs">×</button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <label className="btn-ghost cursor-pointer">
          {pending.length > 0 || (log && log.photos.length > 0) ? "Add photos" : "Snap / choose photos"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => {
              const input = e.currentTarget;
              const list = input.files;
              if (list && list.length > 0) {
                const arr = Array.from(list).filter((f) => f.size > 0);
                if (arr.length > 0) setPending((cur) => [...cur, ...arr].slice(0, 6));
              }
              setTimeout(() => { try { input.value = ""; } catch { /* ignore */ } }, 0);
            }}
          />
        </label>
        <button type="button" className="btn-ghost" disabled={parsing || pending.length === 0} onClick={onAiFill}>
          {parsing ? "Reading…" : "✨ AI fill bean from photo"}
        </button>
      </div>
    </div>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div className="border border-paper-300 bg-paper-100 text-paper-800 px-3 py-2 text-sm flex items-center gap-3">
      <span className="inline-block w-3 h-3 border-2 border-ink border-t-transparent rounded-full animate-spin" />
      {children}
    </div>
  );
}

function toInput(log: CafeLog): Partial<CafeLogInput> {
  const { id, photos, created_at, updated_at, ...rest } = log;
  void id; void photos; void created_at; void updated_at;
  return rest;
}
